'use client';

import React, { useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { card } from 'styled-system/recipes';

import { Loop, Reveal, useDesignMotion } from '@shaojiang/components/motion';
import { Media } from '@shaojiang/components/site/Media';
import { fill } from '@shaojiang/content';
import type { Feature } from '@shaojiang/types/profile';

import { heroCard } from './styles';

interface FeatureTabsProps {
  features: Feature[];
  /** Shown when the project has no features */
  cover: { src: string; alt: string };
  /** "Feature {n}" */
  kicker: string;
  /** Tab list label ("Features") */
  label: string;
  /** Left half of the hero card: status, name, tagline, buttons */
  intro: React.ReactNode;
  /** Between the hero and the tabs (facts, notices) */
  between?: React.ReactNode;
  /** Inside the feature card, under the text (stat tiles) */
  below?: React.ReactNode;
}

const ease = [0.22, 1, 0.36, 1] as const;

/**
 * Studio's project header and feature explorer: the hero screenshot follows the chosen feature,
 * picked from a sticky list of tabs; the feature's story cross-fades in a card beside it.
 */
export function FeatureTabs({
  features,
  cover,
  kicker,
  label,
  intro,
  between,
  below,
}: FeatureTabsProps) {
  const [active, setActive] = useState(0);
  const motion = useDesignMotion();
  const feature = features[active];
  const image = feature ? { src: feature.url, alt: feature.name } : cover;

  return (
    <>
      <div
        className={cx(
          heroCard,
          css({
            p: 'clamp(2rem, 4vw, 3.5rem)',
            display: 'grid',
            gap: '8',
            alignItems: 'center',
            gridTemplateColumns: { base: '1fr', lg: '5fr 7fr' },
          }),
        )}
      >
        <Loop
          preset='breathe'
          aria-hidden
          className={css({
            position: 'absolute',
            w: '520px',
            h: '520px',
            right: '0',
            top: '-80px',
            borderRadius: 'full',
            bg: 'radial-gradient(circle, {colors.glow}, transparent 62%)',
            filter: 'blur(24px)',
            pointerEvents: 'none',
          })}
        />
        <div
          className={css({
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '5',
          })}
        >
          {intro}
        </div>
        <Reveal immediate delay={0.12} className={css({ position: 'relative' })}>
          <div className={cx(card().root, css({ p: '2.5', boxShadow: 'raised' }))}>
            <div className={css({ position: 'relative', aspectRatio: '16 / 10' })}>
              <AnimatePresence initial={false} mode='popLayout'>
                <m.div
                  key={image.src}
                  className={css({ position: 'absolute', inset: '0' })}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease }}
                >
                  <Media
                    src={image.src}
                    alt={image.alt}
                    priority={active === 0}
                    sizes='(min-width: 992px) 58vw, 100vw'
                    className={css({ h: 'full' })}
                  />
                </m.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>

      {between}

      {feature && (
        <div
          className={css({
            display: 'grid',
            gap: '6',
            alignItems: 'start',
            gridTemplateColumns: { base: '1fr', lg: '4fr 8fr' },
          })}
        >
          <Reveal
            role='tablist'
            aria-label={label}
            className={cx(
              card().root,
              css({ p: '3', gap: '1.5', lg: { position: 'sticky', top: '24' } }),
            )}
          >
            <p
              className={css({
                textStyle: 'label',
                color: 'fg.subtle',
                px: '3',
                pt: '2.5',
                pb: '1',
              })}
            >
              {label}
            </p>
            {features.map((f, i) => (
              <button
                key={f.name}
                type='button'
                role='tab'
                aria-selected={i === active}
                onClick={() => setActive(i)}
                className={css({
                  position: 'relative',
                  isolation: 'isolate',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5',
                  w: 'full',
                  px: '4',
                  py: '3.5',
                  textAlign: 'start',
                  borderRadius: 'l2',
                  border: 'none',
                  bg: 'transparent',
                  cursor: 'pointer',
                  color: 'fg.muted',
                  transition: 'color {durations.base}',
                  _hover: { color: 'fg.default' },
                  '&[aria-selected=true]': { color: 'bg.canvas' },
                })}
              >
                {i === active && (
                  <m.span
                    layoutId='studio-feature-tab'
                    transition={motion.layout}
                    className={css({
                      position: 'absolute',
                      inset: '0',
                      zIndex: '-1',
                      borderRadius: 'inherit',
                      bg: 'fg.default',
                    })}
                  />
                )}
                <span className={css({ textStyle: 'sm', fontWeight: '700' })}>{f.name}</span>
                <span className={css({ textStyle: 'xs', opacity: '0.75' })}>
                  {fill(kicker, { n: String(i + 1).padStart(2, '0') })}
                </span>
              </button>
            ))}
          </Reveal>
          <Reveal
            delay={0.06}
            className={cx(card().root, css({ p: '8', gap: '3.5', overflow: 'visible' }))}
          >
            <AnimatePresence mode='wait' initial={false}>
              <m.div
                key={feature.name}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease }}
                className={css({ display: 'flex', flexDirection: 'column', gap: '3.5' })}
              >
                <p className={css({ textStyle: 'label', color: 'fg.accent' })}>
                  {fill(kicker, { n: String(active + 1).padStart(2, '0') })}
                </p>
                <h2 className={css({ textStyle: 'h2' })}>{feature.name}</h2>
                <p className={css({ textStyle: 'md', color: 'fg.muted' })}>{feature.description}</p>
              </m.div>
            </AnimatePresence>
            {below}
          </Reveal>
        </div>
      )}
    </>
  );
}
